import { Component, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { TrialService } from "../../../../../core/services/trial.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Countries } from "../../../../../core/constants/countries";
import { TrialPolicy } from "../../trial-policy.model";
import { AlertService } from "../../../../../shared/alert/alert.service";
import { SiteService } from "../../../../../core/services/site.service";
import { SiteListItem } from "../../../../../core/models/site-list.model";
import { ModalComponent } from "../../../../../shared/modal/modal.component";
import {
  AutosuggestDropdownInputComponent
} from "../../../../../shared/autosuggest-dropdown-input/autosuggest-dropdown-input.component";

@Component({
  selector: 'app-trial-policy-clone',
  templateUrl: './trial-policy-clone.component.html',
  styleUrl: './trial-policy-clone.component.scss'
})
export class TrialPolicyCloneComponent {
  @ViewChild('modal') modal: ModalComponent;
  @ViewChild('countrySelect') countrySelect: AutosuggestDropdownInputComponent;

  @Output('cloned') cloned = new EventEmitter();

  private readonly _trialService = inject(TrialService);
  private readonly _alertService = inject(AlertService);
  private readonly _siteService = inject(SiteService);

  protected form = new FormGroup({
    isBusy: new FormControl<boolean>(false),
    country: new FormControl<string>('', Validators.required),
    sites: new FormControl<string[]>([]),
  });
  countryOptions: { value: string, text: string }[] = [];
  siteOptions: { value: string, text: string }[] = [];
  trialId: string;
  policyId: string
  policy: TrialPolicy;
  sourceCountry: string;
  sourceSiteIds: string[] = [];

  constructor() {
    for (let country of Countries.all()) {
      this.countryOptions.push({ value: country.code, text: country.name });
    }

    this.form.get('country').valueChanges.subscribe(country => {
      if (country)
        this.loadSites(country);
    });
  }

  show(trialId: string, policyId: string) {
    this.form.reset();
    this.countrySelect.onClear();
    this.trialId = trialId;
    this.policyId = policyId;
    this.loadPolicy();
    this.modal.show();
  }

  loadPolicy() {
    this._trialService.getPolicy(this.trialId, this.policyId).subscribe({
      next: (policy: TrialPolicy) => {
        this.policy = policy;

        this.sourceCountry = policy.countryCode;
        this.sourceSiteIds = policy.sites.map(s => s.id);

        this.form.patchValue({
          sites: policy.sites.map(s => s.id)
        });

        setTimeout(() => {
          this.countrySelect.setSelectedOption(policy.countryCode);
        }, 10);
      },
      error: (error) => {
        this._alertService.showErrorAlert(error);
      }
    });
  }

  hasBeenChanged() {
    const countryChanged = this.sourceCountry !== this.form.get('country').value;
    const sitesChanged = JSON.stringify(this.sourceSiteIds.sort()) !== JSON.stringify(this.form.get('sites').value.sort());
    return countryChanged || sitesChanged;
  }

  loadSites(country: string) {
    if (!country || country === '')
      return;

    this.siteOptions = [];
    this._siteService.retrieveSites(1, this.trialId, '', country, false, null, 9999).subscribe({
      next: (sites: any) => {
        if (sites.length === 0 || country !== this.sourceCountry) {
          this.form.patchValue({sites: []});
        }
        sites.results.forEach(site => {
          this.siteOptions.push({ text: this.getSiteNameIncludeSiteNumber(site), value: site.id });
        });
      },
      error: (error) => {
        this._alertService.showErrorAlert(error);
      }
    });
  }

  getSiteNameIncludeSiteNumber(site: SiteListItem) {
    const siteNumber = site.trials[0].siteNumber;

    return siteNumber ? siteNumber + ' ' + site.name : site.name;
  }

  onFormSubmit() {
    if (!this.form.valid)
      return;

    if (!this.hasBeenChanged()) {
      this._alertService.showWarningAlert('You must make a change');
      return;
    }

    this._trialService.cloneTrialPolicy(this.policyId, this.form.get('country').value, this.form.get('sites').value).subscribe({
      next: () => {
        this._alertService.showSuccessAlert('Policy Successfully Cloned');
        this.modal.hide();
        this.cloned.emit();
      },
      error: (error) => {
        this._alertService.showErrorAlert(error);
      }
    })
  }

}
